import { BiasType } from 'libs/end-to-end/src/bias-type';
import { Insight } from 'libs/end-to-end/src/insights';
import { InsightWithFocusArea } from 'libs/end-to-end/src/insights';
import { InsightsResult } from 'libs/end-to-end/src/insights';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { ImpactFactor } from 'libs/end-to-end/src/impact-factor';
import { KeyTakeawaySummaryItem } from 'libs/end-to-end/src/key-takeaways';
import { Treatment } from 'libs/end-to-end/src/treatment';
import { getCalibrationScore } from 'libs/end-to-end/src/tests-utils';
import { guardExhaustive } from 'libs/end-to-end/src/generic';

export const byPriority = (
  a: KeyTakeawaySummaryItem,
  b: KeyTakeawaySummaryItem,
) => {
  return (a?.priority ?? 0) - (b?.priority ?? 0);
};

const impactWeightMap: Record<ImpactFactor, number> = {
  Low: 1,
  Medium: 2,
  High: 3,
};

const byProjectedSavingsOrImpact = (a: Treatment, b: Treatment): number => {
  if (a.projectedSavings && b.projectedSavings) {
    return b.projectedSavings.max - a.projectedSavings.max;
  }

  return impactWeightMap[b.impactFactor] - impactWeightMap[a.impactFactor];
};

const byRecommendationsLength = (a: Treatment, b: Treatment): number => {
  return b.recommendations.length - a.recommendations.length;
};

export const topAchievementInsights = ({
  insights,
  focusArea,
  metric,
  allFocusAreaDefinitions,
}: {
  insights: InsightsResult | null;
  focusArea?: FocusArea;
  metric: string | null;
  allFocusAreaDefinitions: MyFocusAreaDefinition[];
}): InsightWithFocusArea[] => {
  if (!insights) {
    return [];
  }

  const insightsWithFocusAreas: InsightWithFocusArea[] = [];

  const rawInsights = Object.entries(insights ?? {}).flatMap(
    ([focusAreaKey, insights]) =>
      insights.map((insight) => ({
        insight,
        focusArea: FocusArea.parse(focusAreaKey),
      })),
  );

  for (const insight of rawInsights) {
    const definition = allFocusAreaDefinitions.find(
      (definition) => definition.focusArea === insight.focusArea,
    );

    if (!definition) {
      continue;
    }

    const metricDefinition = definition.metrics.find(
      (m) => m.columnHeaderValue === insight.insight.metric,
    );

    if (!metricDefinition) {
      continue;
    }

    insightsWithFocusAreas.push({
      insight: insight.insight,
      focusArea: definition,
      metricDefinition,
    });
  }

  return insightsWithFocusAreas
    .filter((insightWithFocusArea) => {
      if (focusArea && insightWithFocusArea.focusArea.focusArea !== focusArea) {
        return false;
      }

      if (metric !== null && insightWithFocusArea.insight.metric !== metric) {
        return false;
      }

      const type = getInsightsType({
        insight: insightWithFocusArea.insight,
        metricDefinition: insightWithFocusArea.metricDefinition,
      });

      return type === 'achievements';
    })
    .sort((a, b) => a.insight.priority - b.insight.priority)
    .slice(0, 3);
};

export const getInsightsType = ({
  insight,
  metricDefinition,
}: {
  insight: Insight;
  metricDefinition: MyMetricDefinition;
}): 'achievements' | 'risks' => {
  if (insight.type === 'bias') {
    // This should never happen, but it is still in the enum for now.
    return 'risks';
  }

  if (insight.type === 'representation') {
    // This should never happen, but it is still in the enum for now.
    return 'achievements';
  }

  if (insight.type === 'optimization') {
    const calibrationScore = getCalibrationScore({
      testCount: insight.totalTestCount ?? {
        passing: 0,
        failing: 0,
        incomplete: 0,
      },
    });

    if (calibrationScore >= 5.4) {
      return 'achievements';
    }

    return 'risks';
  }

  if (insight.type === 'parity') {
    return 'achievements';
  }

  if (insight.type === 'disparity') {
    if (metricDefinition.calibrationFraming === 'Negative') {
      return insight.highlight?.type === 'higher' ? 'risks' : 'achievements';
    } else if (metricDefinition.calibrationFraming === 'Positive') {
      return insight.highlight?.type === 'lower' ? 'risks' : 'achievements';
    } else {
      throw guardExhaustive(metricDefinition.calibrationFraming);
    }
  }

  throw guardExhaustive(insight.type);
};

export const topRiskInsights = ({
  insights,
  focusArea,
  metric,
  allFocusAreaDefinitions,
}: {
  insights: InsightsResult | null;
  focusArea?: FocusArea;
  metric: string | null;
  allFocusAreaDefinitions: MyFocusAreaDefinition[];
}): InsightWithFocusArea[] => {
  if (!insights) {
    return [];
  }

  const insightsWithFocusAreas: InsightWithFocusArea[] = [];

  const rawInsights = Object.entries(insights ?? {}).flatMap(
    ([focusAreaKey, insights]) =>
      insights.map((insight) => ({
        insight,
        focusArea: FocusArea.parse(focusAreaKey),
      })),
  );

  for (const insight of rawInsights) {
    const definition = allFocusAreaDefinitions.find(
      (definition) => definition.focusArea === insight.focusArea,
    );

    if (!definition) {
      continue;
    }

    const metricDefinition = definition.metrics.find(
      (m) => m.columnHeaderValue === insight.insight.metric,
    );

    if (!metricDefinition) {
      continue;
    }

    insightsWithFocusAreas.push({
      insight: insight.insight,
      focusArea: definition,
      metricDefinition,
    });
  }

  return insightsWithFocusAreas
    .filter((insightWithFocusArea) => {
      if (focusArea && insightWithFocusArea.focusArea.focusArea !== focusArea) {
        return false;
      }

      if (metric !== null && insightWithFocusArea.insight.metric !== metric) {
        return false;
      }

      const type = getInsightsType({
        insight: insightWithFocusArea.insight,
        metricDefinition: insightWithFocusArea.metricDefinition,
      });

      return type === 'risks';
    })
    .sort((a, b) => a.insight.priority - b.insight.priority)
    .slice(0, 3);
};

export interface topTreatmentFilterParams {
  focusArea?: FocusArea;
  biasType?: BiasType;
  dimensionKey?: string;
  levelKeys?: string[];
  metric?: string;
}

/**
 * Filter logic for determining if a treatment should be included in the top treatments list
 */
export const topTreatmentFilter =
  ({
    focusArea,
    biasType,
    dimensionKey,
    levelKeys,
    metric,
  }: topTreatmentFilterParams) =>
  // eslint-disable-next-line complexity
  (treatment: Treatment) => {
    let passesFilter = true;

    if (focusArea) {
      passesFilter = passesFilter && treatment.focusArea === focusArea;
    }
    if (biasType) {
      passesFilter = passesFilter && treatment.biasType === biasType;
    }
    if (dimensionKey) {
      passesFilter = passesFilter && treatment.dimension === dimensionKey;
    }
    if (levelKeys?.length && levelKeys.length > 0) {
      passesFilter = passesFilter && levelKeys.includes(treatment.level);
    }

    if (metric) {
      passesFilter = passesFilter && treatment.metric === metric;
    }

    return passesFilter;
  };

/**
 * @note the sorts could be combined if perf was an issue, but we don't expect more than 100 items in the array and sortign twice is more readable than a complex single sort on three properties
 */
export const topTreatments = ({
  myObservation,
  focusArea,
  biasType,
  dimensionKey,
  levelKeys,
  metric,
}: {
  myObservation: MyObservation | null;
  focusArea?: FocusArea;
  biasType?: BiasType;
  dimensionKey?: string;
  levelKeys?: string[];
  metric?: string;
}): Treatment[] => {
  const allTreatments = myObservation?.treatments || [];
  const hasFilter = Boolean(
    focusArea || biasType || dimensionKey || levelKeys?.length || metric,
  );

  if (hasFilter) {
    return allTreatments
      .filter(
        topTreatmentFilter({
          focusArea,
          biasType,
          dimensionKey,
          levelKeys,
          metric,
        }),
      )
      .sort(byProjectedSavingsOrImpact)
      .sort(byRecommendationsLength)
      .slice(0, 6);
  }

  return [...allTreatments]
    .sort(byProjectedSavingsOrImpact)
    .sort(byRecommendationsLength)
    .slice(0, 6);
};
